// Préparer l'entretien sur la fiche (issue #163). Visible quand la candidature
// est à l'étape « entretien » ; la préparation se demande, elle ne se génère
// jamais seule (un appel IA). Même mise en forme que la relance à J+7.
//
// SOURCÉE depuis l'issue #209 quand la formule le permet (`sourcee`) : Radar
// cherche l'actualité de l'employeur et sa façon de recruter, en tâche de
// fond (une minute environ, la fiche relit toutes les 5 s), et chaque question
// dit d'où elle vient. Sinon, la préparation de l'issue #163, sans recherche.
// Une offre d'agence ne nomme pas l'employeur : le bloc le dit et propose de
// l'indiquer, sans rien bloquer.

import { useEffect, useState } from 'react'
import { MessagesSquare } from 'lucide-react'
import type { EntretienVerifie, SourceRelue } from '@ai-job-fit/engine/rechercheSourcee'
import { get, post } from '../lib/api'
import { useT } from '../lib/i18n'
import { useData, ActionButton, useToast } from './common'
import { CompteurRecherches, LigneSource, domaine, type PermisRecherches } from './remuneration'

interface Preparation extends Partial<Omit<EntretienVerifie, 'questions' | 'aPoser'>> {
  questions: { question: string; pourquoi: string; reponse: string; origine?: 'offre' | 'rapportee'; source?: SourceRelue }[]
  aPoser: string[]
  creeLe: string
}

interface EtatEntretien {
  preparation: Preparation | null
  etat: 'pret' | 'en_cours' | 'erreur'
  sourcee: boolean
  permis?: PermisRecherches
}

const ATTENTE_MS = 5000

export function BlocEntretien({ candidatureId }: { candidatureId: number }) {
  const t = useT()
  const toast = useToast()
  const etat = useData(() => get<EtatEntretien>(`/api/candidatures/${candidatureId}/entretien`), [candidatureId])
  const [neuve, setNeuve] = useState<Preparation | null>(null)
  const [employeur, setEmployeur] = useState('')
  const enCours = etat.data?.etat === 'en_cours'
  useEffect(() => {
    if (!enCours) return
    const i = window.setInterval(() => etat.reload(), ATTENTE_MS)
    return () => window.clearInterval(i)
  }, [enCours])
  // Une préparation neuve sans recherche arrive dans la réponse ; une sourcée
  // arrive au journal, relu par `etat`.
  const prep = (enCours ? null : neuve) ?? etat.data?.preparation ?? null
  const sourcee = etat.data?.sourcee ?? false
  const preparer = async (avecEmployeur?: string) => {
    try {
      const r = await post<{ preparation?: Preparation; etat?: string }>(
        `/api/candidatures/${candidatureId}/entretien`,
        avecEmployeur ? { employeur: avecEmployeur } : {}
      )
      if (r.preparation) setNeuve(r.preparation)
      else {
        setNeuve(null)
        etat.reload()
      }
    } catch (e: any) {
      toast(e.message, 'err')
    }
  }
  const avecSources = prep && prep.actualite !== undefined

  let corps
  if (enCours) {
    corps = (
      <p className="remu-attente" role="status">
        <span className="spinner" />{t('entretien.enCours')}
      </p>
    )
  } else if (!prep) {
    corps = (
      <>
        {etat.data?.etat === 'erreur' && <p className="remu-alerte">{t('entretien.erreur')}</p>}
        <p className="field-help">{t(sourcee ? 'entretien.aideSourcee' : 'entretien.aide')}</p>
        <div className="relance-act">
          <ActionButton variant="primary" busyLabel={t('entretien.redaction')} onClick={() => preparer()}>
            {t('entretien.preparer')}
          </ActionButton>
        </div>
        <CompteurRecherches permis={etat.data?.permis ?? null} />
      </>
    )
  } else {
    corps = (
      <>
        {avecSources && !prep.employeurReel && sourcee && (
          <div className="remu-couche">
            <p className="field-help">{t('entretien.agence')}</p>
            <div className="relance-act">
              <input
                className="input-field"
                value={employeur}
                onChange={(e) => setEmployeur(e.target.value)}
                placeholder={t('entretien.employeurNom')}
                aria-label={t('entretien.employeurNom')}
                maxLength={120}
              />
              <ActionButton size="sm" disabled={!employeur.trim()} onClick={() => preparer(employeur)}>
                {t('entretien.avecEmployeur')}
              </ActionButton>
            </div>
          </div>
        )}
        {avecSources && prep.actualite!.length === 0 && (prep.deroule ?? []).length === 0 && <p className="field-help">{t('entretien.rienTrouve')}</p>}
        {avecSources && prep.actualite!.length > 0 && (
          <div className="remu-couche">
            <b>{t('entretien.actualite')}</b>
            <ul className="remu-liste">
              {prep.actualite!.map((a, i) => (
                <li key={i}>
                  {a.fait}
                  <LigneSource s={a.source} />
                </li>
              ))}
            </ul>
          </div>
        )}
        {avecSources && (prep.deroule ?? []).length > 0 && (
          <div className="remu-couche">
            <b>{t('entretien.deroule')}</b>
            <ul className="remu-liste">
              {prep.deroule!.map((a, i) => (
                <li key={i}>
                  {a.fait}
                  <LigneSource s={a.source} />
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="remu-couche">
          {avecSources && <b>{t('entretien.questions')}</b>}
          <ol className="entretien-qs">
            {prep.questions.map((q, i) => (
              <li key={i}>
                <b>{q.question}</b>
                {q.pourquoi && <div className="field-help">{q.pourquoi}</div>}
                {q.reponse && <p>{q.reponse}</p>}
                {q.source && (
                  <div className="field-help">
                    {q.origine === 'rapportee' ? (
                      <>
                        {t('entretien.rapportee')} · <a href={q.source.url ?? undefined} target="_blank" rel="noopener noreferrer">{domaine(q.source.url)}</a>
                      </>
                    ) : (
                      <>
                        {t('entretien.tireeOffre')} <q>{q.source.extrait}</q>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
        {prep.aPoser.length > 0 && (
          <div className="remu-couche">
            <b>{t('entretien.aPoser')}</b>
            <ul className="entretien-aposer">
              {prep.aPoser.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </div>
        )}
        <div className="relance-act">
          <ActionButton size="sm" busyLabel={t('entretien.redaction')} onClick={() => preparer()}>
            {t('entretien.refaire')}
          </ActionButton>
        </div>
      </>
    )
  }

  return (
    <section className="card relance entretien" data-testid="bloc-entretien">
      <div className="relance-h">
        <MessagesSquare size={18} />
        <b>{t('entretien.titre')}</b>
      </div>
      {corps}
    </section>
  )
}
